import { useState, useMemo } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Menu,
  X,
  ChevronLeft,
  LogOut,
} from 'lucide-react';
import { LayoutContext } from '../../contexts/LayoutContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getVisibleAdminNav, type NavItem } from '../../config/navigation';

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hideSidebar, setHideSidebar] = useState(false);
  const { role, name, signOut } = useAdminAuth();

  // Filter nav items based on user role (driven by config/navigation.ts)
  const visibleItems = useMemo(() => getVisibleAdminNav(role), [role]);

  return (
    <LayoutContext.Provider value={{ hideSidebar, setHideSidebar }}>
    <div className="admin-theme h-screen bg-bg-deep text-text-primary font-sans flex overflow-hidden">
      {/* Desktop sidebar — hidden during immersive review mode */}
      {!hideSidebar && (
        <aside className="hidden md:flex flex-col w-60 bg-bg-card border-r border-bg-tertiary flex-shrink-0 overflow-y-auto">
          <SidebarContent items={visibleItems} />
          {/* Sign out at bottom of sidebar */}
          <div className="px-3 py-3 border-t border-bg-tertiary">
            {name && (
              <p className="text-xs text-text-muted px-3 mb-2 truncate">{name}</p>
            )}
            <button
              onClick={signOut}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors w-full"
            >
              <LogOut size={15} />
              Sign Out
            </button>
          </div>
        </aside>
      )}

      {/* Mobile sidebar overlay — pure CSS transitions */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden sidebar-backdrop anim-fade-in"
            onClick={() => setSidebarOpen(false)}
          />
          <aside
            className="fixed left-0 top-0 bottom-0 w-60 bg-bg-card border-r border-bg-tertiary z-50 md:hidden flex flex-col sidebar-drawer"
            style={{ animation: 'adminSlideInLeft 0.2s ease-out both' }}
          >
            <div className="flex items-center justify-end p-3">
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarContent items={visibleItems} onNavClick={() => setSidebarOpen(false)} />
            {/* Mobile sign out */}
            <div className="px-3 py-3 border-t border-bg-tertiary">
              {name && (
                <p className="text-xs text-text-muted px-3 mb-2 truncate">{name}</p>
              )}
              <button
                onClick={signOut}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors w-full"
              >
                <LogOut size={15} />
                Sign Out
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar — compact in immersive review mode */}
        <header className={`flex items-center gap-3 px-4 md:px-6 bg-bg-card border-b border-bg-tertiary flex-shrink-0 ${hideSidebar ? 'py-1.5' : 'py-3'}`}>
          {!hideSidebar && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary md:hidden"
            >
              <Menu size={20} />
            </button>
          )}
          <NavLink to="/" className="p-2 rounded-lg hover:bg-bg-elevated text-text-secondary" title="Back to app">
            <ChevronLeft size={20} />
          </NavLink>
          {!hideSidebar && (
            <h1 className="text-xl font-display">
              <span className="text-gold">Hemologix</span>{' '}
              <span className="text-text-secondary">Control Center</span>
            </h1>
          )}
          {hideSidebar && (
            <span className="text-sm text-text-muted">Attorney Review Mode</span>
          )}
        </header>

        {/* Scrollable content — top padding on inner div so sticky elements sit flush */}
        <main className={`flex-1 overflow-y-auto bg-bg-deep ${hideSidebar ? 'px-2 pb-2 md:px-3 md:pb-3' : 'px-4 pb-4 md:px-6 md:pb-6'}`}>
          <div className={hideSidebar ? 'pt-2 md:pt-3' : 'pt-4 md:pt-6'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
    </LayoutContext.Provider>
  );
}

function SidebarContent({ items, onNavClick }: { items: NavItem[]; onNavClick?: () => void }) {
  return (
    <nav className="flex-1 py-4">
      <div className="px-4 mb-6">
        <p className="text-sm uppercase tracking-wider text-text-muted font-semibold">Formulary Admin</p>
      </div>
      <ul className="space-y-1 px-2">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors ${
                  isActive
                    ? 'bg-bg-elevated text-gold font-medium'
                    : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
